import { Controller } from "@hotwired/stimulus"

const MAP_ID = "jobs-view-map"
const DATA_SCRIPT_ID = "jobs-view-map-data"
const US_GEOGRAPHIC_CENTER = { lat: 39.8283, lng: -98.5795 }

// Jobs index map: multi-marker Mapbox map with job card hover sync.
export default class extends Controller {
  static targets = ["container", "popupTemplate"]
  static values = {
    accessToken: String,
    planroomId: String,
  }

  connect() {
    this.abortController = new AbortController()
    this.cardListeners = []
    this.initialize()
  }

  disconnect() {
    this.abortController.abort()
    this.cardListeners.forEach(({ element, type, handler }) => {
      element.removeEventListener(type, handler)
    })
    this.cardListeners = []
    this.teardown()
  }

  get mapsManager() {
    return window.mapboxManager
  }

  initialize() {
    const manager = this.mapsManager
    if (!manager) {
      console.error("Maps Manager not available")
      this.containerTarget.innerHTML =
        '<div class="flex items-center justify-center h-full text-gray-500">Maps Manager not loaded</div>'
      return
    }

    if (this.isMapContainerVisible()) {
      this.loadMapDirectly()
    } else {
      manager.registerLazyMap(this.containerTarget, {}, MAP_ID, () => {
        this.createJobsMap()
      })
    }
  }

  teardown() {
    const manager = this.mapsManager
    if (!manager) return

    if (manager.intersectionObserver) {
      manager.intersectionObserver.unobserve(this.containerTarget)
    }
    manager.lazyMaps?.delete(this.containerTarget)
    manager.destroyMap(MAP_ID)
  }

  isMapContainerVisible() {
    const computedStyle = window.getComputedStyle(this.containerTarget)
    if (computedStyle.display === "none" || computedStyle.visibility === "hidden") {
      return false
    }

    const rect = this.containerTarget.getBoundingClientRect()
    return rect.width > 0 && rect.height > 0
  }

  async loadMapDirectly() {
    try {
      await this.mapsManager.loadAPI(this.accessTokenValue)
      if (this.abortController.signal.aborted) return
      this.createJobsMap()
    } catch (error) {
      console.error("Failed to load maps:", error)
      this.containerTarget.innerHTML =
        '<div class="flex items-center justify-center h-full text-gray-500">Failed to load map service</div>'
    }
  }

  readJobsData() {
    const el = document.getElementById(DATA_SCRIPT_ID)
    if (!el?.textContent?.trim()) return []
    try {
      return JSON.parse(el.textContent)
    } catch {
      return []
    }
  }

  filterJobsWithLocation(jobs) {
    return jobs.filter((job) => job.location_lat && job.location_lng)
  }

  calculateJobsCenter(jobs) {
    if (jobs.length === 0) return US_GEOGRAPHIC_CENTER

    if (jobs.length === 1) {
      return {
        lat: parseFloat(jobs[0].location_lat),
        lng: parseFloat(jobs[0].location_lng),
      }
    }

    let minLat = parseFloat(jobs[0].location_lat)
    let maxLat = parseFloat(jobs[0].location_lat)
    let minLng = parseFloat(jobs[0].location_lng)
    let maxLng = parseFloat(jobs[0].location_lng)

    for (const job of jobs) {
      const lat = parseFloat(job.location_lat)
      const lng = parseFloat(job.location_lng)
      minLat = Math.min(minLat, lat)
      maxLat = Math.max(maxLat, lat)
      minLng = Math.min(minLng, lng)
      maxLng = Math.max(maxLng, lng)
    }

    return {
      lat: (minLat + maxLat) / 2,
      lng: (minLng + maxLng) / 2,
    }
  }

  createJobsMap() {
    if (this.abortController.signal.aborted) return

    const manager = this.mapsManager
    const jobsData = this.readJobsData()
    const jobsWithLocation = this.filterJobsWithLocation(jobsData)

    if (jobsWithLocation.length === 0) {
      console.warn("No jobs with location data found")
      return
    }

    const center = this.calculateJobsCenter(jobsWithLocation)
    const newestJobUpdatedAt =
      jobsData
        .map((job) => job.updated_at)
        .filter(Boolean)
        .sort((a, b) => new Date(b) - new Date(a))[0] || null

    const cacheKey = `jobs-map-${this.planroomIdValue || "default"}-${jobsWithLocation.length}-${new Date().toISOString()}`

    this.createMapboxJobsMap(manager, jobsWithLocation, center, cacheKey, newestJobUpdatedAt)
  }

  createMapboxJobsMap(manager, jobsWithLocation, center, cacheKey, newestJobUpdatedAt) {
    const map = manager.createMapWithCache(
      this.containerTarget,
      {
        style: "mapbox://styles/mapbox/streets-v12",
        center: [center.lng, center.lat],
        zoom: 6,
        cacheKey,
      },
      MAP_ID,
      newestJobUpdatedAt
    )

    if (window.mapboxgl?.NavigationControl) {
      map.addControl(new window.mapboxgl.NavigationControl(), "top-right")
    }

    map.on("load", () => {
      if (this.abortController.signal.aborted) return

      const mapMarkers = []
      const mapPopups = []

      jobsWithLocation.forEach((job, index) => {
        const el = document.createElement("div")
        el.className = "mapbox-marker"
        el.style.width = "32px"
        el.style.height = "32px"
        el.style.cursor = "pointer"

        const img = document.createElement("img")
        img.src = "/map-pin.png"
        img.style.width = "100%"
        img.style.height = "100%"
        img.style.display = "block"
        img.alt = job.name || "Location"
        el.appendChild(img)

        const marker = manager.createMarker({ element: el, anchor: "bottom" })
        marker.setLngLat([parseFloat(job.location_lng), parseFloat(job.location_lat)])
        marker.addTo(map)
        mapMarkers.push(marker)

        const popupContent = this.buildMapboxPopupContent(job)
        const popup = manager
          .createPopup({
            maxWidth: "240px",
            closeButton: false,
            closeOnClick: false,
            offset: [0, -15],
          })
          .setHTML(popupContent)

        mapPopups.push(popup)

        const clickHandler = () => {
          mapPopups.forEach((item) => {
            if (item.isOpen()) item.remove()
          })
          popup.setLngLat([parseFloat(job.location_lng), parseFloat(job.location_lat)])
          popup.addTo(map)
          this.wireMapboxPopupActions(popup, job)
        }

        marker.getElement().addEventListener("click", clickHandler)
        marker.getElement().addEventListener("mouseenter", clickHandler)

        this.registerJobCardHover(index, () => {
          map.flyTo({
            center: [parseFloat(job.location_lng), parseFloat(job.location_lat)],
            zoom: Math.max(map.getZoom(), 7),
            duration: 500,
          })
          mapPopups.forEach((item) => {
            if (item.isOpen()) item.remove()
          })
          clickHandler()
        })
      })

      const existingMapData = manager.activeMaps.get(MAP_ID)
      if (existingMapData) {
        existingMapData.markers = mapMarkers
        existingMapData.popups = mapPopups
      }
    })
  }

  buildMapboxPopupContent(job) {
    const template = this.hasPopupTemplateTarget ? this.popupTemplateTarget : document.getElementById("mapbox-popup-template")
    if (!template) return ""

    const popupElement = template.content.cloneNode(true)
    const popupJobNameLinkElement = popupElement.querySelector('[data-js="popup-job-link"]')
    const popupJobNameElement = popupJobNameLinkElement?.querySelector('[data-js="popup-job-name"]')
    const popupJobLocationElement = popupElement.querySelector('[data-js="popup-job-location"]')
    const popupJobLinkElement = popupElement.querySelector('[data-js="popup-job-link"]')

    if (popupJobNameElement) popupJobNameElement.textContent = job.name || "Unknown Project"
    if (popupJobLocationElement) popupJobLocationElement.textContent = job.location || "Unknown Location"
    if (popupJobLinkElement) {
      popupJobLinkElement.dataset.jobUrl = `/pages/jobs/${job.id}/view/show`
    }

    const tempContainer = document.createElement("div")
    tempContainer.appendChild(popupElement)
    return tempContainer.innerHTML
  }

  wireMapboxPopupActions(popup, job) {
    const closeButton = popup.getElement().querySelector('[data-js="popup-close"]')
    if (closeButton) {
      closeButton.addEventListener("click", (event) => {
        event.stopPropagation()
        popup.remove()
      })
    }

    const popupJobLinkElement = popup.getElement().querySelector('[data-js="popup-job-link"]')
    if (popupJobLinkElement) {
      popupJobLinkElement.addEventListener("click", () => {
        const popupJobLinkUrl = popupJobLinkElement.dataset.jobUrl
        if (popupJobLinkUrl) window.location.href = popupJobLinkUrl
      })
    }
  }

  registerJobCardHover(index, handler) {
    const jobCard = document.querySelector(`[data-js="job-card"][data-index="${index}"]`)
    if (!jobCard) return

    jobCard.addEventListener("mouseenter", handler)
    this.cardListeners.push({ element: jobCard, type: "mouseenter", handler })
  }
}
